import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { hasAdminPrivileges } from '../../utils/permissionsClient';
import Icon from './icons';
import ProfileMenu, { Avatar } from './ProfileMenu';
import CommandPalette from './CommandPalette';
import logo from '../DashboardHome/rush-logo.png';
import { hasActiveFocusSession } from '../RushPages/MyQueue/FocusSession';

const FOCUS_PULSE_CSS = `
@keyframes rs-focus-pulse {
    0%, 100% { transform: scale(1); filter: drop-shadow(0 0 0 rgba(255, 107, 61, 0)); }
    50% { transform: scale(1.18); filter: drop-shadow(0 0 6px rgba(255, 107, 61, 0.65)); }
}
.rs-side-item.rs-side-focus .ic,
.rs-side-item.rs-side-focus.active .ic {
    color: #ff6b3d;
    animation: rs-focus-pulse 1.4s ease-in-out infinite;
    transform-origin: center;
}
.rs-side-item.rs-side-focus .ic svg { overflow: visible; }
.rs-side-item.rs-side-locked { opacity: 0.72; }
.rs-side-item.rs-side-locked:hover { opacity: 1; }
.rs-side-lock {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    color: var(--ink-4, #9aa0a6);
}
.rs-side-item.active .rs-side-lock { color: inherit; }
`;
if (typeof document !== 'undefined' && !document.getElementById('rs-focus-pulse-css')) {
    const s = document.createElement('style');
    s.id = 'rs-focus-pulse-css';
    s.textContent = FOCUS_PULSE_CSS;
    document.head.appendChild(s);
}

// Small lock glyph shown on plan-gated nav items as an upsell cue.
const LOCK_ICON = (
    <svg viewBox="0 0 14 14" width="12" height="12" fill="none" aria-hidden="true">
        <rect x="2.75" y="6" width="8.5" height="6" rx="1.4" stroke="currentColor" strokeWidth="1.3" />
        <path d="M4.75 6V4.5a2.25 2.25 0 014.5 0V6" stroke="currentColor" strokeWidth="1.3" />
    </svg>
);

const SECTIONS = (counts) => [
    {
        label: 'Workspace',
        items: [
            ['Overview', Icon.home, null, '/dashboard'],
            ['All projects', Icon.folder, counts.projects, '/dashboard/projects'],
            ['My queue', Icon.bolt, counts.queue, '/dashboard/queue'],
            ['Inbox', Icon.inbox, counts.inbox, '/dashboard/inbox'],
            ['AI quality', Icon.gauge, null, '/dashboard/ai-quality'],
            ['Reports', Icon.chart, null, '/dashboard/reports'],
        ],
    },
    {
        label: 'Knowledge',
        items: [
            ['Translation memory', Icon.memory, null, '/dashboard/translation-memory'],
            ['Glossaries', Icon.glossary, null, '/dashboard/glossaries'],
            ['Brand voice', Icon.voice, null, '/dashboard/brand-voice', 'brandVoice'],
            ['Automations', Icon.flow, null, '/dashboard/automations'],
            ['In-context preview', Icon.eye, null, '/dashboard/preview'],
            ['Integrations', Icon.link, null, '/dashboard/sources'],
        ],
    },
    {
        label: 'Team',
        items: [
            ['People', Icon.people, null, '/dashboard/people'],
            ['Settings', Icon.gear, null, '/dashboard/settings'],
        ],
    },
];

const Sidebar = () => {
    const location = useLocation();
    const [cmdOpen, setCmdOpen] = useState(false);
    const [counts, setCounts] = useState({ projects: null, queue: null, inbox: null });
    const [inFocus, setInFocus] = useState(() => hasActiveFocusSession());
    // Effective (billing-user) plan features — drives the lock cue on gated
    // nav items. Empty until /me resolves; items are never locked while unknown.
    const [planFeatures, setPlanFeatures] = useState(null);
    // Whether the current user may see management-only nav (Reports). null until
    // /me resolves; we only hide once we positively know they are NOT a manager.
    const [isManager, setIsManager] = useState(null);

    useEffect(() => {
        const recompute = () => setInFocus(hasActiveFocusSession());
        window.addEventListener('rush:focus-changed', recompute);
        // Catches focus state changes from other tabs
        const onStorage = (e) => { if (!e.key || e.key === 'rush.focusSession') recompute(); };
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener('rush:focus-changed', recompute);
            window.removeEventListener('storage', onStorage);
        };
    }, []);
    const user = useSelector((s) => s.auth.user) || {};
    const token = useSelector((s) => s.auth.token);
    const isAdmin = hasAdminPrivileges(user);

    useEffect(() => {
        const h = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setCmdOpen((o) => !o);
            }
        };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    }, []);

    useEffect(() => {
        if (!token) return undefined;
        const headers = { Authorization: `Bearer ${token}` };

        // Full refresh (projects + notifications) — used on mount.
        const refreshAll = () => {
            Promise.all([
                axios.get('/api/projects?summary=true', { headers }).catch(() => ({ data: {} })),
                axios.get('/api/notifications', { headers }).catch(() => ({ data: [] })),
            ]).then(([projRes, notifRes]) => {
                const { createdProjects = [], sharedProjects = [] } = projRes.data || {};
                const unique = new Map([...createdProjects, ...sharedProjects].map((p) => [p._id, p]));
                const projects = [...unique.values()];
                const active = projects.filter((p) => !p.completed).length;
                const mine = projects.filter((p) => !p.completed && (p.owner === user._id || p.createdBy === user._id)).length;
                const inbox = (notifRes.data || []).filter((n) => !n.read).length;
                setCounts({
                    projects: active ? String(active) : null,
                    queue: mine ? String(mine) : null,
                    inbox: inbox ? String(inbox) : null,
                });
            });
        };

        // Lightweight refresh — just the unread inbox count. Used when a
        // socket event arrives so the badge updates without re-fetching the
        // whole projects list on every notification.
        const refreshInbox = () => {
            axios.get('/api/notifications', { headers })
                .then((r) => {
                    const inbox = (r.data || []).filter((n) => !n.read).length;
                    setCounts((c) => ({ ...c, inbox: inbox ? String(inbox) : null }));
                })
                .catch(() => { /* soft fail */ });
        };

        refreshAll();

        // Subscribe to the same realtime events the Inbox page listens for
        // so the sidebar badge stays current without a page reload. Falls
        // back gracefully if the global socket isn't attached yet.
        const socket = typeof window !== 'undefined' ? window.__SOCKET__ : null;
        if (!socket) return undefined;
        socket.on('commentMention', refreshInbox);
        socket.on('notification:created', refreshInbox);
        return () => {
            socket.off('commentMention', refreshInbox);
            socket.off('notification:created', refreshInbox);
        };
    }, [token, user._id]);

    // Resolve effective plan features for the lock cue on gated nav items.
    useEffect(() => {
        if (!token) return;
        axios.get('/api/users/me', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                setPlanFeatures((r.data && r.data.effectivePlan && r.data.effectivePlan.features) || {});
                setIsManager(r.data ? r.data.isManager !== false : null);
            })
            .catch(() => { /* soft fail — leave items unlocked */ });
    }, [token]);

    let sections = [...SECTIONS(counts)];
    // Reports is a workspace-wide, manager-only view. Hide it once we know the
    // user is not a manager (linguists, proofreaders, reviewers, guests, …).
    if (isManager === false) {
        sections = sections.map((g) => ({
            ...g,
            items: g.items.filter(([, , , href]) => href !== '/dashboard/reports'),
        }));
    }
    if (isAdmin) {
        sections.push({
            label: 'Workspace admin',
            items: [['Admin console', Icon.shield, null, '/dashboard/admin']],
        });
    }

    const isActive = (href) => {
        if (href === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/dashboard/';
        return location.pathname.startsWith(href);
    };

    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'You';
    const workspace = user.organization || user.company || 'Workspace';

    return (
        <aside className="rs-side">
            <div className="rs-side-brand">
                <img src={logo} alt="Rush Studio" />
            </div>

            <div
                className="rs-side-search"
                role="button"
                tabIndex={0}
                onClick={() => setCmdOpen(true)}
                onKeyDown={(e) => { if (e.key === 'Enter') setCmdOpen(true); }}
            >
                {Icon.search}
                <span className="text">Quick find…</span>
                <span className="kbd">⌘K</span>
            </div>

            {sections.map((g) => (
                <React.Fragment key={g.label}>
                    <div className="rs-side-section">{g.label}</div>
                    {g.items.map(([label, ic, badge, href, feature]) => {
                        const active = isActive(href);
                        const featureLocked = !!feature && planFeatures && planFeatures[feature] === false;
                        return (
                            <Link
                                key={label}
                                to={href}
                                className={`rs-side-item ${active ? 'active' : ''} ${label === 'My queue' && inFocus ? 'rs-side-focus' : ''} ${featureLocked ? 'rs-side-locked' : ''}`}
                                style={{ textDecoration: 'none' }}
                                title={featureLocked ? `${label} is a premium feature — upgrade to unlock` : (label === 'My queue' && inFocus ? 'Focus session in progress' : undefined)}
                            >
                                <span className="ic">{ic}</span>
                                <span>{label}</span>
                                {featureLocked && <span className="rs-side-lock">{LOCK_ICON}</span>}
                                {badge && !featureLocked && (
                                    <span className={`badge ${label === 'Inbox' ? 'dot' : ''}`}>{badge}</span>
                                )}
                            </Link>
                        );
                    })}
                </React.Fragment>
            ))}

            <ProfileMenu
                placement="up"
                trigger={(open) => (
                    <div className={`rs-side-foot ${open ? 'open' : ''}`} role="button" tabIndex={0}>
                        <Avatar user={user} className="av-me" />
                        <div className="who">
                            <b>{name}</b>
                            <span>{workspace}</span>
                        </div>
                        <span className="opts">{Icon.more}</span>
                    </div>
                )}
            />

            {cmdOpen && <CommandPalette onClose={() => setCmdOpen(false)} />}
        </aside>
    );
};

export default Sidebar;
